import { Skeleton } from '@/components/ui/skeleton';
import { apiClient } from '@/lib/apiClient';
import { getMediaUrl } from '@/lib/media';
import { cn } from '@/lib/utils';
import { type Category } from '@/types';
import { Link } from '@inertiajs/react';
import { useEffect, useMemo, useState } from 'react';

const MOST_SEARCHED_ENDPOINT = '/api/categories/most-searched';
const LOADING_ITEMS_COUNT = 5;
const CARD_BASE_CLASS = cn(
    'group relative flex overflow-hidden rounded-lg border border-border bg-card',
    'w-64 shrink-0 snap-start md:w-full md:shrink md:snap-none',
);
const INTERACTIVE_CARD_CLASS =
    'cursor-pointer transition-transform transition-shadow duration-200 hover:-translate-y-0.5 hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 focus-visible:ring-offset-2';

interface FallbackCategory {
    name: string;
    svg: string;
}

interface MostSearchedCategoriesProps {
    fallbackCategories?: FallbackCategory[];
}

type MostSearchedCategory = Omit<Category, 'parent'> & {
    parent?: MostSearchedCategory;
    friendly_url_path?: string | null;
};

type MostSearchedCardItem =
    | {
          key: string;
          kind: 'loading';
      }
    | {
          key: string;
          kind: 'category';
          name: string;
          iconSrc: string | null;
          href?: string;
      };

interface MostSearchedResponse {
    data: MostSearchedCategory[];
    success: boolean;
}

const buildLoadingItems = (): MostSearchedCardItem[] =>
    Array.from({ length: LOADING_ITEMS_COUNT }, (_, index) => ({
        key: `loading-${index}`,
        kind: 'loading',
    }));

const buildFallbackItems = (fallbackCategories: FallbackCategory[]): MostSearchedCardItem[] =>
    fallbackCategories.map((category, index) => ({
        key: `fallback-${index}`,
        kind: 'category',
        name: category.name,
        iconSrc: category.svg,
    }));

const getCategoryIcon = (category: MostSearchedCategory): string | null => {
    let current: MostSearchedCategory | undefined = category;
    const visitedCategoryIds = new Set<number>();

    while (current) {
        if (visitedCategoryIds.has(current.id)) {
            break;
        }

        if (current.icone) {
            return getMediaUrl(current.icone, 'card');
        }

        visitedCategoryIds.add(current.id);
        current = current.parent;
    }

    return null;
};

const buildCategoryItems = (categories: MostSearchedCategory[]): MostSearchedCardItem[] =>
    categories.map((category) => ({
        key: String(category.id),
        kind: 'category',
        name: category.name,
        iconSrc: getCategoryIcon(category),
        href: category.friendly_url_path ?? undefined,
    }));

interface MostSearchedCategoryCardProps {
    item: MostSearchedCardItem;
}

function MostSearchedCategoryCard({ item }: MostSearchedCategoryCardProps) {
    const isLink = item.kind === 'category' && Boolean(item.href);
    const cardClassName = cn(CARD_BASE_CLASS, isLink && INTERACTIVE_CARD_CLASS);

    const content = (
        <div className={cn('flex h-auto w-full p-4', item.kind === 'loading' && 'animate-pulse')}>
            <div className="mr-3 w-1 shrink-0 self-stretch rounded-full bg-primary" />
            <div className="h-auto content-center">
                {item.kind === 'loading' ? (
                    <div className="space-y-2">
                        <Skeleton className="h-7 w-3/4 rounded-md" />
                    </div>
                ) : (
                    <h3 className="line-clamp-2 text-base leading-tight font-extrabold tracking-tight whitespace-pre-line text-foreground uppercase">
                        {item.name}
                    </h3>
                )}
            </div>
        </div>
    );

    if (item.kind === 'category' && item.href) {
        return (
            <Link aria-label={`Ver anúncios de ${item.name}`} className={cardClassName} href={item.href}>
                {content}
            </Link>
        );
    }

    return <div className={cardClassName}>{content}</div>;
}

export function MostSearchedCategories({ fallbackCategories = [] }: MostSearchedCategoriesProps) {
    const [categories, setCategories] = useState<MostSearchedCategory[]>([]);
    const [status, setStatus] = useState<'loading' | 'success' | 'fallback'>('loading');

    useEffect(() => {
        const abortController = new AbortController();

        const fetchMostSearched = async () => {
            try {
                const response = await apiClient.get<MostSearchedResponse>(MOST_SEARCHED_ENDPOINT, {
                    signal: abortController.signal,
                });

                if (response.data.success && response.data.data.length > 0) {
                    setCategories(response.data.data);
                    setStatus('success');
                    return;
                }

                setStatus('fallback');
            } catch (error) {
                if ((error as { name?: string }).name === 'CanceledError') {
                    return;
                }

                console.error('Error fetching most searched categories:', error);
                setStatus('fallback');
            }
        };

        fetchMostSearched();

        return () => {
            abortController.abort();
        };
    }, []);

    const items = useMemo<MostSearchedCardItem[]>(() => {
        if (status === 'loading') {
            return buildLoadingItems();
        }

        if (status === 'fallback') {
            return buildFallbackItems(fallbackCategories);
        }

        return buildCategoryItems(categories);
    }, [categories, fallbackCategories, status]);

    return (
        <section className="pb-16">
            <div className="mx-6 lg:mx-24">
                <h2 className="mb-6 text-2xl leading-none font-bold tracking-normal text-foreground">Mais buscados</h2>

                <div className="-mx-1 overflow-x-auto overflow-y-hidden pb-2 md:mx-0 md:overflow-visible md:pb-0">
                    <div className="flex w-max snap-x snap-mandatory gap-4 px-1 md:grid md:w-full md:snap-none md:grid-cols-2 md:px-0 lg:grid-cols-3 xl:grid-cols-5">
                        {items.map((item) => (
                            <MostSearchedCategoryCard key={item.key} item={item} />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
